import { useCallback, useEffect, useMemo, useState } from 'react';

import { isCarouselOffer, Offer, useOffersCarouselCategories } from '@ocx/data-offers';
import { useRudderStack } from '@ocx/data-analytics';
import { useMembership } from '@ocx/data-membership';
// eslint-disable-next-line @nx/enforce-module-boundaries -- app configuration
import { useConfiguration } from '@ocx-app/modules/configuration/useConfiguration';

export type UseOfferCategoriesOptions = {
  persistToStorage?: boolean;
};

export const useOfferCategories = (offers: Offer[], options: UseOfferCategoriesOptions = {}) => {
  const { persistToStorage } = options;
  const { triggerEvent } = useRudderStack();
  const {
    config: { chainId },
  } = useConfiguration();
  const { membership } = useMembership();

  const carouselOffers = useMemo(() => offers.filter(isCarouselOffer), [offers]);
  const membershipRestrictionVerification = membership.fuelAndConvenienceProfile?.restrictionVerification;
  const { categories, refetchCategories } = useOffersCarouselCategories(
    carouselOffers,
    { chainId, membershipRestrictionVerification },
    { persistToStorage },
  );
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const selectedCategory = useMemo(
    () => categories.find(({ id }) => id === selectedCategoryId) || null,
    [categories, selectedCategoryId],
  );

  const handleCategoryChanged = useCallback(
    (nextCategoryId: string) => {
      const nextCategory = categories.find(({ id }) => id === nextCategoryId);
      if (!nextCategory || nextCategoryId === selectedCategoryId) {
        return;
      }

      triggerEvent('home_categories', {
        displayCategoryId: nextCategoryId,
        displayCategoryTitle: nextCategory.title,
      });
      setSelectedCategoryId(nextCategoryId);
    },
    [categories, selectedCategoryId, triggerEvent],
  );

  useEffect(() => {
    // Select the first category as soon as one is available. We deliberately do NOT wait
    // for external banners to load: that would mean a blank body on cached navigation
    // (Apollo's `loading` stays true during background revalidation under
    // `cache-and-network`), which is the more common UX cost.
    //
    // Accepted trade-off: on a cold load where the first display category is banner-only,
    // it is filtered out of the list until its banners arrive (see
    // `groupOfferAndCategories`). The user briefly sees the next category selected; once
    // banners land the banner-only category appears in front but selection stays where it
    // was (`find(id===…)` still matches). Manual click corrects it.
    //
    // Self-correction: if the list later changes and the selected id is dropped,
    // `selectedCategory` becomes null via the find-by-id memo above and this effect
    // re-runs to pick the new first.
    if (selectedCategory || !categories[0]) {
      return;
    }
    handleCategoryChanged(categories[0].id);
  }, [selectedCategory, handleCategoryChanged, categories]);

  return useMemo(() => {
    return {
      categories,
      selectedCategory,
      onCategoryChange: handleCategoryChanged,
      refetchCategories,
    };
  }, [categories, handleCategoryChanged, selectedCategory, refetchCategories]);
};
